import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import {
  listSystemConfig,
  updateSystemConfigValue,
} from '@/api/system_config'
import { SMS_TEMPLATE_DEFAULTS, SMS_TEMPLATE_KEYS, type SmsTemplates } from '@/api/sms'
import { countSmsSegments } from '@/utils/sms'
import { PageHeader } from '@/components/layout/PageHeader'
import { Loader } from '@/components/ui/Loader'

/**
 * /sms-settings — «Авито → SMS». Редактор текстов SMS клиенту (заказчику
 * осмотра). Источник: ключи sms_template_* в public.system_config (миграция
 * 20260603120000). Эти же значения читают и авто-триггеры БД, и кнопки на
 * карточке лида — один источник, без дрейфа.
 *
 * В шаблоне обязателен плейсхолдер {link} — на его место код подставляет
 * ссылку (на форму /z/ или статус /s/). Сам URL менять нельзя — только
 * формулировку вокруг ссылки.
 */

const SAMPLE_TOKEN = 'AbCdEf1234'

interface FieldMeta {
  field: keyof SmsTemplates
  key: string
  sampleLink: string
}

const FIELDS: FieldMeta[] = [
  {
    field: 'lead',
    key: SMS_TEMPLATE_KEYS.lead,
    sampleLink: `https://avs.carlink.ru/z/${SAMPLE_TOKEN}`,
  },
  {
    field: 'paid',
    key: SMS_TEMPLATE_KEYS.paid,
    sampleLink: `https://avs.carlink.ru/s/${SAMPLE_TOKEN}`,
  },
  {
    field: 'status',
    key: SMS_TEMPLATE_KEYS.status,
    sampleLink: `https://avs.carlink.ru/s/${SAMPLE_TOKEN}`,
  },
  {
    field: 'report',
    key: SMS_TEMPLATE_KEYS.report,
    sampleLink: `https://avs.carlink.ru/s/${SAMPLE_TOKEN}`,
  },
  {
    field: 'assigned',
    key: SMS_TEMPLATE_KEYS.assigned,
    sampleLink: `https://avs.carlink.ru/s/${SAMPLE_TOKEN}`,
  },
]

export function SmsSettingsPage() {
  const { t } = useTranslation()
  const qc = useQueryClient()

  const { data, isLoading, error } = useQuery({
    queryKey: ['system-config', 'sms-templates'],
    queryFn: listSystemConfig,
    staleTime: 30_000,
  })

  // Текущие значения из БД по ключам (или дефолт, если ключа/значения нет).
  const dbValues = useMemo<Record<string, string>>(() => {
    const byKey = new Map((data ?? []).map((e) => [e.key, e.value]))
    const out: Record<string, string> = {}
    for (const f of FIELDS) {
      const v = (byKey.get(f.key) ?? '').trim()
      out[f.key] = v || SMS_TEMPLATE_DEFAULTS[f.field]
    }
    return out
  }, [data])

  const [draft, setDraft] = useState<Record<string, string>>({})
  const [errorMsg, setErrorMsg] = useState<string | null>(null)

  const valueOf = (key: string): string => draft[key] ?? dbValues[key] ?? ''

  const mut = useMutation({
    mutationFn: async (changed: Array<{ key: string; value: string }>) => {
      for (const c of changed) {
        await updateSystemConfigValue(c.key, c.value)
      }
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['system-config', 'sms-templates'] })
      void qc.invalidateQueries({ queryKey: ['sms-templates'] }) // кнопки на лиде
      setDraft({})
      setErrorMsg(null)
    },
    onError: (e: Error) => setErrorMsg(e.message),
  })

  // Изменённые поля (отличаются от значения в БД).
  const changed = FIELDS.filter((f) => draft[f.key] != null && draft[f.key] !== dbValues[f.key])
  const isDirty = changed.length > 0
  // Запрет сохранения, если хоть одно изменённое поле без {link} или пустое.
  const hasInvalid = changed.some((f) => {
    const v = valueOf(f.key).trim()
    return v === '' || !v.includes('{link}')
  })

  return (
    <div>
      <PageHeader
        title={t('settings.sms.title')}
        subtitle={t('settings.sms.subtitle')}
      />

      {isLoading ? (
        <Loader />
      ) : error ? (
        <Banner kind="error" text={t('settings.error', { message: (error as Error).message })} />
      ) : (
        <div className="flex flex-col gap-6">
          {FIELDS.map((f) => (
            <TemplateField
              key={f.key}
              meta={f}
              value={valueOf(f.key)}
              onChange={(v) =>
                setDraft((prev) => {
                  const next = { ...prev, [f.key]: v }
                  if (next[f.key] === dbValues[f.key]) delete next[f.key]
                  return next
                })
              }
            />
          ))}

          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => {
                if (!isDirty || hasInvalid) return
                setErrorMsg(null)
                mut.mutate(changed.map((f) => ({ key: f.key, value: valueOf(f.key).trim() })))
              }}
              disabled={!isDirty || hasInvalid || mut.isPending}
              className="rounded bg-[color:var(--stryzh-color-accent-orange)] px-4 py-2 text-sm font-semibold text-black transition disabled:opacity-40"
            >
              {mut.isPending
                ? t('settings.saving')
                : isDirty
                  ? t('settings.sms.saveWithCount', { count: changed.length })
                  : t('common.save')}
            </button>
            <button
              type="button"
              onClick={() => {
                setDraft({})
                setErrorMsg(null)
              }}
              disabled={!isDirty || mut.isPending}
              className="rounded border border-[color:var(--stryzh-color-border-default)] px-4 py-2 text-sm text-[color:var(--stryzh-color-text-secondary)] hover:bg-white/5 disabled:opacity-40"
            >
              {t('common.cancel')}
            </button>
            {hasInvalid ? (
              <span className="text-xs text-red-300">
                {t('settings.sms.linkRequired')}
              </span>
            ) : null}
          </div>

          {errorMsg ? <Banner kind="error" text={errorMsg} /> : null}
        </div>
      )}
    </div>
  )
}

function TemplateField({
  meta,
  value,
  onChange,
}: {
  meta: FieldMeta
  value: string
  onChange: (v: string) => void
}) {
  const { t } = useTranslation()
  const hasLink = value.includes('{link}')
  const isEmpty = value.trim() === ''
  const preview = value.split('{link}').join(meta.sampleLink)
  const segments = countSmsSegments(preview)
  const invalid = isEmpty || !hasLink

  return (
    <div className="rounded-lg border border-[color:var(--stryzh-color-border-default)] bg-[color:var(--stryzh-color-surface)] p-4">
      <div className="mb-3">
        <div className="flex flex-wrap items-baseline gap-2">
          <div className="text-base font-semibold text-[color:var(--stryzh-color-text-primary)]">
            {t(`settings.sms.field.${meta.field}.label`)}
          </div>
          <span className="text-xs text-[color:var(--stryzh-color-text-secondary)]">
            {t('settings.sms.placeholderMap', { target: t(`settings.sms.field.${meta.field}.linkLabel`) })}
          </span>
        </div>
        <div className="mt-1 text-xs text-[color:var(--stryzh-color-text-secondary)]">{t(`settings.sms.field.${meta.field}.hint`)}</div>
      </div>

      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={2}
        className="w-full resize-y rounded border border-[color:var(--stryzh-color-border-default)] bg-[color:var(--stryzh-color-void)] px-3 py-2 font-mono text-sm text-[color:var(--stryzh-color-text-primary)]"
      />

      <div className="mt-2">
        <div className="text-[11px] uppercase tracking-wide text-[color:var(--stryzh-color-text-secondary)]">
          {t('settings.sms.previewLabel', { token: SAMPLE_TOKEN })}
        </div>
        <div className="mt-1 rounded border border-[color:var(--stryzh-color-border-default)] bg-[color:var(--stryzh-color-surface-elevated)] p-2 text-sm">
          <code className="break-all font-mono">{preview}</code>
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-3 text-xs text-[color:var(--stryzh-color-text-secondary)]">
          <span>
            {t(segments === 1 ? 'settings.sms.charCountOne' : 'settings.sms.charCountMany', {
              chars: preview.length,
              segments,
            })}
            {segments > 1 ? t('settings.sms.tariffMultiplier', { segments }) : ''}
          </span>
          {invalid ? (
            <span className="text-red-300">
              {isEmpty ? t('settings.sms.empty') : t('settings.sms.noPlaceholder')}
            </span>
          ) : null}
        </div>
      </div>
    </div>
  )
}

function Banner({ kind, text }: { kind: 'error' | 'muted'; text: string }) {
  const cls =
    kind === 'error'
      ? 'border-red-500/40 bg-red-500/10 text-red-200'
      : 'border-white/10 bg-white/5 text-[color:var(--stryzh-color-text-secondary)]'
  return <div className={`rounded border p-4 text-sm ${cls}`}>{text}</div>
}
